import { BaseFeature } from '../../core/base-feature.js';

/**
 * ScreenTintFeature - Frontend feature for screen tint overlay control
 * Allows admin to tint the desktop screen with configurable color, opacity, and effects.
 */
export class ScreenTintFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        this.state = {
            isActive: false,
            config: null
        };

        this.stateChangeCallbacks = new Set();
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'screen-tint',
            name: 'Screen Tint',
            version: 'v1.0',
            capabilities: ['screen-tint'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[ScreenTint] Initializing...');
        console.log('[ScreenTint] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                return this.handleStateUpdate(payload);

            case 'enable_response':
                return this.handleResponse('enable', payload);
            case 'disable_response':
                return this.handleResponse('disable', payload);
            case 'update_response':
                return this.handleResponse('update', payload);
            case 'get_state_response':
                return this.handleResponse('get_state', payload);

            default:
                console.warn(`[ScreenTint] Unknown command: ${command}`);
        }
    }

    // =====================================
    // State Handlers
    // =====================================

    handleStateUpdate(payload) {
        if (payload) {
            this.state = { ...this.state, ...payload };
            this.notifyStateChangeListeners();
        }
    }

    handleResponse(requestType, payload) {
        const resolve = this._pendingRequests.get(requestType);
        if (resolve) {
            this._pendingRequests.delete(requestType);
            resolve(payload);
        }
    }

    // =====================================
    // Public API
    // =====================================

    async enable(config) {
        return new Promise((resolve) => {
            this.sendMessage('enable', config || {});
            this._pendingRequests.set('enable', resolve);
        });
    }

    async disable() {
        return new Promise((resolve) => {
            this.sendMessage('disable', {});
            this._pendingRequests.set('disable', resolve);
        });
    }

    async update(config) {
        return new Promise((resolve) => {
            this.sendMessage('update', config || {});
            this._pendingRequests.set('update', resolve);
        });
    }

    async getState() {
        return new Promise((resolve) => {
            this.sendMessage('get_state', {});
            this._pendingRequests.set('get_state', resolve);
        });
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    onStateChange(callback) {
        this.stateChangeCallbacks.add(callback);
        return () => this.stateChangeCallbacks.delete(callback);
    }

    notifyStateChangeListeners() {
        for (const callback of this.stateChangeCallbacks) {
            try {
                callback(this.state);
            } catch (error) {
                console.error('[ScreenTint] Error in state change callback:', error);
            }
        }
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[ScreenTint] Cleaning up...');
        this.state = { isActive: false, config: null };
        this.stateChangeCallbacks.clear();
        this._pendingRequests.clear();
    }
}
